import { useCallback, useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import clsx from 'clsx';
import debounce from 'lodash.debounce';

import TaskContext from '@atom/components/workOrderPreview/taskView/TaskContext';
import WorkOrderPreviewContext from '@atom/components/workOrderPreview/WorkOrderPreviewContext';
import { TASK_UPDATE } from '@atom/graph/task';
import { Icon, TextField } from '@atom/mui';
import { SubjectIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';

import classes from './taskView.module.css';

const DEBOUNCE_TIME = 1000;

const styles = {
  icon: {
    paddingTop: '0.5rem',
  },
  textField: {
    maxWidth: '31.25rem',
    color: colors.neutral.dim,
  },
};

const TaskDescription = () => {
  const { workOrderDetail, refetch } = useContext(WorkOrderPreviewContext);
  const { task } = useContext(TaskContext);

  const [description, setDescription] = useState<string>(
    task?.description || '',
  );

  useEffect(() => {
    setDescription(task?.description || '');
  }, [task.id]);

  const [taskUpdate] = useMutation(TASK_UPDATE);

  const progressiveUpdate = useCallback(
    debounce(async (value: string) => {
      await taskUpdate({
        variables: {
          input: {
            id: task.id,
            workOrderId: workOrderDetail.id,
            description: value,
          },
        },
      });

      refetch();
    }, DEBOUNCE_TIME),
    [workOrderDetail.id, task.id],
  );

  const handleChange = event => {
    setDescription(event.target.value);
    progressiveUpdate(event.target.value);
  };

  const isDisabled =
    workOrderDetail.isClosed || doesNotHaveRolePermissions(ROLE_SETS.INSPECTOR);

  return (
    <>
      <Icon
        style={styles.icon}
        color={colors.neutral.gray}
        icon={SubjectIcon}
      />
      <div className={clsx(classes.taskDetailRowContent, classes.label)}>
        <TextField
          id={description}
          label="Description"
          value={description}
          disabled={isDisabled}
          onChange={handleChange}
          style={styles.textField}
          multiline
          inputLabelColor={colors.neutral.dim}
        />
      </div>
    </>
  );
};

export default TaskDescription;
